import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Scale, Sun, Brain, Heart, Dumbbell } from "lucide-react";

const transformations = [
  {
    category: "Weight Loss",
    icon: Scale,
    name: "Sarah M., 46",
    timeline: "12 weeks on Semaglutide protocol",
    stats: [{ label: "Lost", value: "32 lbs" }, { label: "Waist", value: "-5 in" }, { label: "Energy", value: "+80%" }],
    quote: "I finally feel like myself again. The weight came off steadily and I never felt deprived.",
    beforeImg: "https://images.unsplash.com/photo-1634463278803-f9f71890e67d?w=400&h=500&fit=crop&crop=center",
    afterImg: "https://images.unsplash.com/photo-1655268802812-b0496f2ff3c9?w=400&h=500&fit=crop&crop=center",
    color: "from-emerald-500/20 to-emerald-500/5",
    accent: "text-emerald-700",
    bg: "bg-emerald-50",
  },
  {
    category: "Skin & Anti-Aging",
    icon: Sun,
    name: "Karen W., 43",
    timeline: "8 weeks on GHK-Cu + custom protocol",
    stats: [{ label: "Skin Score", value: "+45%" }, { label: "Collagen", value: "+30%" }, { label: "Glow", value: "Radiant" }],
    quote: "People keep asking what I'm doing differently. My skin hasn't looked this good since my 20s.",
    beforeImg: "https://images.unsplash.com/photo-1775498638177-66461931d968?w=400&h=500&fit=crop&crop=center",
    afterImg: "https://images.unsplash.com/photo-1519084278803-b94f11e1c63b?w=400&h=500&fit=crop&crop=center",
    color: "from-amber-500/20 to-amber-500/5",
    accent: "text-amber-700",
    bg: "bg-amber-50",
  },
  {
    category: "Energy & Vitality",
    icon: Zap,
    name: "James C., 53",
    timeline: "10 weeks on CJC-1295 + Ipamorelin",
    stats: [{ label: "Energy", value: "+90%" }, { label: "Sleep", value: "7+ hrs" }, { label: "Focus", value: "Sharp" }],
    quote: "I went from crashing at 2pm every day to running my company with more clarity than I had at 35.",
    beforeImg: "https://images.unsplash.com/photo-1674070260739-21769b924247?w=400&h=500&fit=crop&crop=center",
    afterImg: "https://images.unsplash.com/photo-1744060204728-f68e434a3edf?w=400&h=500&fit=crop&crop=center",
    color: "from-blue-500/20 to-blue-500/5",
    accent: "text-blue-700",
    bg: "bg-blue-50",
  },
  {
    category: "Recovery & Performance",
    icon: Dumbbell,
    name: "Marcus R., 34",
    timeline: "8 weeks on BPC-157 + TB-500",
    stats: [{ label: "Pain", value: "-85%" }, { label: "Mobility", value: "+60%" }, { label: "Training", value: "Full" }],
    quote: "After two ACL tears, I never thought I'd run again. Now I'm doing 5Ks every weekend.",
    beforeImg: "https://images.unsplash.com/photo-1553991050-45b457b749b3?w=400&h=500&fit=crop&crop=center",
    afterImg: "https://images.unsplash.com/photo-1636645369838-bce90cad438e?w=400&h=500&fit=crop&crop=center",
    color: "from-purple-500/20 to-purple-500/5",
    accent: "text-purple-700",
    bg: "bg-purple-50",
  },
];

export default function TransformationsSection() {
  const [active, setActive] = useState(0);
  const t = transformations[active];
  const Icon = t.icon;

  return (
    <section id="transformations" className="py-24 bg-[#F7F5F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-xs font-sans font-medium uppercase tracking-[0.2em] text-[#C47C5D] mb-3 block">
            Real Results
          </span>
          <h2 className="text-3xl md:text-4xl font-serif font-light tracking-tight text-[#2A3324] mb-3">
            Before & After Transformations
          </h2>
          <p className="text-base font-sans font-light text-[#2A3324]/50 max-w-2xl mx-auto">
            Real patients, real results. See how peptide recovery is transforming lives across weight loss, anti-aging, energy, and recovery.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 justify-center mb-12 flex-wrap">
          {transformations.map((tr, i) => {
            const TabIcon = tr.icon;
            return (
              <button
                key={tr.category}
                onClick={() => setActive(i)}
                className={`flex items-center gap-2 text-sm font-sans font-medium px-5 py-2.5 rounded-full transition-all duration-300 ${
                  active === i
                    ? "bg-[#2A3324] text-[#F7F5F0] shadow-lg"
                    : "bg-white border border-[#2A3324]/10 text-[#2A3324]/60 hover:text-[#2A3324] hover:border-[#2A3324]/30"
                }`}
              >
                <TabIcon size={16} />
                {tr.category}
              </button>
            );
          })}
        </div>

        {/* Transformation Card */}
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-3xl border border-[#2A3324]/5 overflow-hidden shadow-sm"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Before/After Images */}
            <div className="grid grid-cols-2 min-h-[400px]">
              {/* Before */}
              <div className="relative overflow-hidden">
                <img src={t.beforeImg} alt="Before" className="w-full h-full object-cover grayscale brightness-90" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <span className="absolute bottom-4 left-4 text-xs font-sans font-medium uppercase tracking-wider text-white/80 bg-black/30 px-3 py-1 rounded-full backdrop-blur-sm">
                  Before
                </span>
              </div>
              {/* After */}
              <div className="relative overflow-hidden">
                <img src={t.afterImg} alt="After" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                <span className={`absolute bottom-4 left-4 text-xs font-sans font-medium uppercase tracking-wider text-white px-3 py-1 rounded-full backdrop-blur-sm bg-emerald-600/70`}>
                  After
                </span>
              </div>
            </div>

            {/* Details */}
            <div className="p-8 md:p-10 flex flex-col justify-center">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${t.bg}`}>
                <Icon size={24} className={t.accent} strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-serif font-light text-[#2A3324] mb-1">{t.name}</h3>
              <p className="text-xs font-sans text-[#2A3324]/40 mb-6">{t.timeline}</p>

              {/* Stats */}
              <div className="flex gap-6 mb-6">
                {t.stats.map((s) => (
                  <div key={s.label}>
                    <p className={`text-2xl font-serif font-light ${t.accent}`}>{s.value}</p>
                    <p className="text-[10px] font-sans uppercase tracking-wider text-[#2A3324]/40">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Quote */}
              <blockquote className="text-base font-sans font-light text-[#2A3324]/70 italic leading-relaxed mb-6 border-l-3 border-[#2A3324]/10 pl-4">
                "{t.quote}"
              </blockquote>

              <p className="text-[10px] font-sans text-[#2A3324]/25 mb-6">
                * Individual results may vary. Results shown are from patients who followed their prescribed peptide protocols consistently.
              </p>

              <a href="#quiz" className="inline-flex items-center gap-2 text-sm font-sans font-medium text-[#C47C5D] hover:text-[#B36B4C] transition-colors w-fit">
                See if you qualify <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
